import type { FC } from 'react';
import React, { Fragment, useMemo } from 'react';
import { useIntl, FormattedMessage, defineMessages } from 'react-intl-next';
// We have deprecated emotion. Please use compiled instead
// eslint-disable-next-line no-restricted-imports, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from '@emotion/styled';
import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { HOME_RECENT_EXPERIENCE } from '@confluence/experience-tracker';
import { PageCards } from '@confluence/page-card';
import { PageSegmentLoadStart, PageSegmentLoadEnd } from '@confluence/browser-metrics';
import { useSessionData } from '@confluence/session-data';
import { ErrorDisplay } from '@confluence/error-boundary';
import { DocumentContentTitle } from '@confluence/document-title';
import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { mergeLiveDocI18n } from '@confluence/live-pages-utils/entry-points/mergeLiveDocI18n';

import { untitled } from '../homeViewsQueryHelpers';
import { Options, useOptions } from '../Options';
import { LoadingAndPagination } from '../LoadingAndPagination';
import { ExperienceTracking } from '../ExperienceTracking';
import { CreatePageButton } from '../CreatePageButton';
import RecentEmptyStateLight from '../images/recent/RecentEmptyStateLight.svg';
import RecentEmptyStateDark from '../images/recent/RecentEmptyStateDark.svg';
import { HOME_RECENTS_VIEW_SEGMENT } from '../../../perf.config';

import { useRecentData } from './useRecentData';
import type { RecentFilter } from './useRecentPreferences';
import {
	useRecentPreferences,
	useAnonymousRecentPreference,
	useRecentPreferencesOverride,
} from './useRecentPreferences';

const i18nBase = defineMessages({
	recentTitle: {
		id: 'home.views.recent.title',
		defaultMessage: 'Recent',
		description: 'Header for the list of recent pages',
	},
	all: {
		id: 'home.views.recent.options.all',
		defaultMessage: 'All',
		description: 'Label for a button to view all the users recent pages',
	},
	workedOn: {
		id: 'home.views.recent.options.worked-on',
		defaultMessage: 'Worked on',
		description: 'Label for a button to view all pages a user has worked on',
	},
	created: {
		id: 'home.views.recent.options.created-by-me',
		defaultMessage: 'Created by me',
		description: 'Label for a button to view all pages a user has created',
	},
	owned: {
		id: 'home.views.recent.options.owned-by-me',
		defaultMessage: 'Owned by me',
		description: 'Label for a button to view all pages a user has owned',
	},
	noRecentResults: {
		id: 'home.views.recent.no-results.no-recent-results',
		defaultMessage:
			// TODO: replace straight quotes with curly quotes (see go/curlyquotes)
			// eslint-disable-next-line no-restricted-syntax
			"We can't find any pages visited in the last 90 days that match.",
		description: 'Header shown when all recent pages have been filtered out',
	},
	noResultsSubtext: {
		id: 'home.views.recent.no-results.no-recent-results-subtext',
		defaultMessage: '<a>Search older pages instead</a>.',
		description: 'message suggesting navigation to advanced search',
	},
	noRecentCreatedByMeResults: {
		id: 'home.views.recent.no-results.no-recent-created-by-me-results',
		// TODO: replace straight quotes with curly quotes (see go/curlyquotes)
		// eslint-disable-next-line no-restricted-syntax
		defaultMessage: "We can't find any pages you've created that match.",
		description: 'Header shown when all recent pages have been filtered out',
	},
	noResultsCreatedByMeSubtext: {
		id: 'home.views.recent.no-results.no-recent-created-by-me-results-subtext',
		defaultMessage: '<a>Search pages created by anyone instead</a>.',
		description: 'message suggesting navigation to advanced search',
	},
	emptyHeader: {
		id: 'home.views.recent.empty.header',
		defaultMessage: 'Get out there and explore',
		description:
			'Descriptive header that appears on the Recent Pages Home view when the user has no visited Pages.',
	},
	emptyBody: {
		id: 'home.views.recent.empty.body',
		defaultMessage: 'Once you visit, edit, or create pages, you’ll find the most recent ones here.',
		description:
			'Descriptive body text that appears on the Recent Pages Home view when the user has no visited Pages.',
	},
});

const i18nLiveDocs = defineMessages({
	noRecentResults: {
		id: 'home.views.recent.no-results.no-recent-results.livedocs',
		defaultMessage: 'We can’t find any pages or live docs visited in the last 90 days that match.',
		description: 'Updated version of home.views.recent.no-results.no-recent-results',
	},

	noResultsSubtext: {
		id: 'home.views.recent.no-results.no-recent-results-subtext.livedocs',
		defaultMessage: '<a>Search older pages and live docs instead</a>.',
		description: 'Updated version of home.views.recent.no-results.no-recent-results-subtext',
	},

	noRecentCreatedByMeResults: {
		id: 'home.views.recent.no-results.no-recent-created-by-me-results.livedocs',
		defaultMessage: 'We can’t find any pages or docs you’ve created that match.',
		description: 'Updated version of home.views.recent.no-results.no-recent-created-by-me-results',
	},

	noResultsCreatedByMeSubtext: {
		id: 'home.views.recent.no-results.no-recent-created-by-me-results-subtext.livedocs',
		defaultMessage: '<a>Search pages and docs created by anyone instead</a>.',
		description:
			'Updated version of home.views.recent.no-results.no-recent-created-by-me-results-subtext',
	},

	emptyBody: {
		id: 'home.views.recent.empty.body.livedocs',
		defaultMessage:
			'Once you visit, edit, or create pages or live docs, you’ll find the most recent ones here.',
		description: 'Updated version of home.views.recent.empty.body',
	},
});

const i18n = mergeLiveDocI18n(i18nBase, i18nLiveDocs);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const RecentContainer = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
`;

const recentContainerStyles = css({
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const RecentHeader = styled.h1`
	font: ${token('font.heading.large')};
	margin-bottom: ${token('space.200')};
`;

const recentHeaderStyles = css({
	font: token('font.heading.large'),
	marginBottom: token('space.200'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const PageCardsContainer = styled.div`
	position: relative;
`;

const pageCardsContainerStyles = css({
	position: 'relative',
});

const recentEmptyStateImage = {
	src: RecentEmptyStateLight,
	srcDark: RecentEmptyStateDark,
};

// used for fromSource attribute in analytics
const FromSource = {
	all: 'null',
	'worked-on': 'worked-on',
	created: 'created',
};

const toPrefs = {
	all: 'ALL',
	'worked-on': 'WORKED_ON',
	created: 'CREATED',
};

const fromPrefs = {
	ALL: 'all',
	WORKED_ON: 'worked-on',
	CREATED: 'created',
};

type RecentComponentProps = {
	filter: RecentFilter;
	updateFilter: (filter) => void;
};

export const Recent = () => {
	const intl = useIntl();
	const { isLoggedIn } = useSessionData();
	const { filterOverride, clearFilterOverride } = useRecentPreferencesOverride();
	const recentPreferences = useRecentPreferences();
	const anonymousRecentPreference = useAnonymousRecentPreference();
	const { loading, queryError, mutationError, filter, updateFilter } = isLoggedIn
		? recentPreferences
		: anonymousRecentPreference;
	const filterOrOverride = filterOverride || filter;
	const updateFilterAndClearOverride = (updatedFilter: RecentFilter) => {
		clearFilterOverride();
		updateFilter(updatedFilter);
	};

	if (fg('confluence_frontend_home_compiled_migration')) {
		return (
			<div data-testid="home_recent" css={recentContainerStyles}>
				<PageSegmentLoadStart metric={HOME_RECENTS_VIEW_SEGMENT} />
				<DocumentContentTitle titlePrefix={intl.formatMessage(i18n.recentTitle)} />
				{/* eslint-disable-next-line @atlaskit/design-system/use-heading */}
				<h1 css={recentHeaderStyles}>
					<FormattedMessage {...i18n.recentTitle} />
				</h1>
				{!loading && (
					<RecentComponent filter={filterOrOverride} updateFilter={updateFilterAndClearOverride} />
				)}
				{queryError && <ErrorDisplay error={queryError} />}
				{mutationError && <ErrorDisplay error={mutationError} />}
			</div>
		);
	}

	return (
		<RecentContainer data-testid="home_recent">
			<PageSegmentLoadStart metric={HOME_RECENTS_VIEW_SEGMENT} />
			<DocumentContentTitle titlePrefix={intl.formatMessage(i18n.recentTitle)} />
			<RecentHeader>
				<FormattedMessage {...i18n.recentTitle} />
			</RecentHeader>
			{!loading && (
				<RecentComponent filter={filterOrOverride} updateFilter={updateFilterAndClearOverride} />
			)}
			{queryError && <ErrorDisplay error={queryError} />}
			{mutationError && <ErrorDisplay error={mutationError} />}
		</RecentContainer>
	);
};

export const RecentComponent: FC<RecentComponentProps> = ({ filter, updateFilter }) => {
	const filterText = expVal<boolean>(
		'confluence_frontend_page_ownership_recent_tab_page',
		'isEnabled',
		false,
	)
		? i18n.owned
		: i18n.created;

	const pills = [
		{ name: 'all', i18n: <FormattedMessage {...i18n.all} /> },
		{ name: 'worked-on', i18n: <FormattedMessage {...i18n.workedOn} /> },
		{ name: 'created', i18n: <FormattedMessage {...filterText} /> },
	];

	const intl = useIntl();
	const defaultPill = useMemo(() => (filter ? fromPrefs[filter] : 'all'), [filter]);

	const optionsProps = useOptions(pills, defaultPill);

	const { pageCardData, loading, hasNextPage, loadMore, error } = useRecentData({
		pill: optionsProps.pill,
		filterText: optionsProps.filterText,
		translatedUntitled: intl.formatMessage(untitled),
	});

	const [noResultsMessage, noResultsSubtext] =
		filter === 'CREATED'
			? [i18n.noRecentCreatedByMeResults, i18n.noResultsCreatedByMeSubtext]
			: [i18n.noRecentResults, i18n.noResultsSubtext];

	const modifiedOptionsProps = {
		...optionsProps,
		setPill: (pill) => {
			updateFilter(toPrefs[pill]);
			// @ts-ignore we always pass pills to useOptions and the conditional was untestable
			optionsProps.setPill(pill);
		},
	};

	const attributes = optionsProps.pill
		? { isFromSource: FromSource[optionsProps.pill] }
		: undefined;

	if (fg('confluence_frontend_home_compiled_migration')) {
		return (
			<Fragment>
				<Options {...modifiedOptionsProps} />
				<div css={pageCardsContainerStyles}>
					{!optionsProps.loading && (
						<PageCards
							appearance={optionsProps.appearance}
							nodes={pageCardData}
							analyticsData={{ source: 'recentScreen', attributes }}
						/>
					)}
					<LoadingAndPagination
						pageCardDataLength={pageCardData.length}
						loading={loading}
						hasNextPage={hasNextPage}
						loadMore={loadMore}
						error={error}
						noResultsMessage={noResultsMessage}
						noResultsSubtext={noResultsSubtext}
						emptyMessageHeader={i18n.emptyHeader}
						emptyMessageBody={i18n.emptyBody}
						emptyMessageImage={recentEmptyStateImage}
						emptyMessageCallToAction={
							<CreatePageButton
								appearance="primary"
								creationLinkSource="home-recent"
								showCreateLivePageButton
							/>
						}
						filterText={optionsProps.filterText}
						source="recentScreen"
						filter={filter}
					/>
					<ExperienceTracking
						experienceName={HOME_RECENT_EXPERIENCE}
						loading={loading}
						error={error}
					/>
					{!loading && <PageSegmentLoadEnd metric={HOME_RECENTS_VIEW_SEGMENT} />}
				</div>
			</Fragment>
		);
	}
	return (
		<Fragment>
			<Options {...modifiedOptionsProps} />
			<PageCardsContainer>
				{!optionsProps.loading && (
					<PageCards
						appearance={optionsProps.appearance}
						nodes={pageCardData}
						analyticsData={{ source: 'recentScreen', attributes }}
					/>
				)}
				<LoadingAndPagination
					pageCardDataLength={pageCardData.length}
					loading={loading}
					hasNextPage={hasNextPage}
					loadMore={loadMore}
					error={error}
					noResultsMessage={noResultsMessage}
					noResultsSubtext={noResultsSubtext}
					emptyMessageHeader={i18n.emptyHeader}
					emptyMessageBody={i18n.emptyBody}
					emptyMessageImage={recentEmptyStateImage}
					emptyMessageCallToAction={
						<CreatePageButton
							appearance="primary"
							creationLinkSource="home-recent"
							showCreateLivePageButton
						/>
					}
					filterText={optionsProps.filterText}
					source="recentScreen"
					filter={filter}
				/>
				<ExperienceTracking
					experienceName={HOME_RECENT_EXPERIENCE}
					loading={loading}
					error={error}
				/>
				{!loading && <PageSegmentLoadEnd metric={HOME_RECENTS_VIEW_SEGMENT} />}
			</PageCardsContainer>
		</Fragment>
	);
};
